import React, { useState, useMemo, useEffect } from "react";
import { Table, notification, Dropdown, Modal, Input, Badge } from "antd";
import { useSearchParams } from "react-router-dom";
import {
  MoreOutlined,
  DeleteOutlined,
  InboxOutlined,
  ExportOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import {
  useGetContactsQuery,
  useGetDemoRequestsQuery,
  useGetSupportTicketsQuery,
  useArchiveContactMutation,
  useUnarchiveContactMutation,
  useDeleteContactMutation,
  useArchiveDemoMutation,
  useUnarchiveDemoMutation,
  useDeleteDemoMutation,
  useUpdateSupportStatusMutation,
  useArchiveSupportMutation,
  useUnarchiveSupportMutation,
  useDeleteSupportMutation,
} from "../../../store/api/apiSlice";
import { ACTIONS_DROPDOWN_PROPS } from "../../../constants/actionsDropdown";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../../store/auth/authSlice";
import { markEnquiriesAsSeen } from "../../../utils/seenEnquiries";
import { useEnquiriesCount } from "../../../hooks/useEnquiriesCount";
import { buildConditionalPagination } from "../../../utils/pagination";

const SUPPORT_STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "resolved", label: "Resolved" },
];

const normalizeSupportStatus = (status) =>
  String(status || "")
    .toLowerCase()
    .replace(/\s+/g, "_");

const getUserId = (user) => user?._id || user?.id || user?.data?._id || null;

const renderTabBadge = (label, count) => (
  <Badge
    count={count}
    size="small"
    overflowCount={99}
    offset={[10, 0]}
    style={{ backgroundColor: "#ef4444" }}
  >
    <span className="pr-1">{label}</span>
  </Badge>
);

const Enquiries = () => {
  const user = useSelector(selectCurrentUser);
  const userId = getUserId(user);
  const { counts: unseenCounts } = useEnquiriesCount();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("contacts");
  const [showArchived, setShowArchived] = useState(false);
  const [supportStatusFilter, setSupportStatusFilter] = useState("all");
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolutionNote, setResolutionNote] = useState("");
  const [viewNotesTarget, setViewNotesTarget] = useState(null);
  const [viewMessageTarget, setViewMessageTarget] = useState(null);

  const { data: contacts, isLoading: contactsLoading } = useGetContactsQuery({
    archived: showArchived,
  });
  const { data: demos, isLoading: demosLoading } = useGetDemoRequestsQuery({
    archived: showArchived,
  });
  const {
    data: supports,
    isLoading: supportsLoading,
    refetch: refetchSupports,
  } = useGetSupportTicketsQuery({ archived: showArchived });

  const [archiveContact] = useArchiveContactMutation();
  const [unarchiveContact] = useUnarchiveContactMutation();
  const [deleteContact] = useDeleteContactMutation();
  const [archiveDemo] = useArchiveDemoMutation();
  const [unarchiveDemo] = useUnarchiveDemoMutation();
  const [deleteDemo] = useDeleteDemoMutation();
  const [updateSupportStatus] = useUpdateSupportStatusMutation();
  const [archiveSupport] = useArchiveSupportMutation();
  const [unarchiveSupport] = useUnarchiveSupportMutation();
  const [deleteSupport] = useDeleteSupportMutation();

  useEffect(() => {
    const tab = searchParams.get("tab");
    const status = searchParams.get("status");

    if (tab === "support" || tab === "contacts" || tab === "demos") {
      setActiveTab(tab);
    }

    if (
      status === "open" ||
      status === "in_progress" ||
      status === "resolved"
    ) {
      setSupportStatusFilter(status);
    } else {
      setSupportStatusFilter("all");
    }
  }, [searchParams]);

  const contactList = contacts?.data || [];
  const supportList = supports?.data || [];
  const demoList = demos?.data || [];

  useEffect(() => {
    if (!userId) return;

    const listByTab = {
      contacts: contactList,
      demos: demoList,
      support: supportList,
    };

    const currentList = listByTab[activeTab];

    if (!currentList) return;

    markEnquiriesAsSeen(
      userId,
      activeTab,
      currentList.map((item) => item._id),
    );
  }, [userId, activeTab, contactList, demoList, supportList]);

  const setEnquiriesTab = (tab) => {
    setActiveTab(tab);

    const params = new URLSearchParams(searchParams);
    params.set("tab", tab);

    if (tab !== "support") {
      params.delete("status");
      setSupportStatusFilter("all");
    }

    setSearchParams(params);
  };

  const setSupportStatus = (status) => {
    setSupportStatusFilter(status);

    const params = new URLSearchParams(searchParams);
    params.set("tab", "support");

    if (status === "all") {
      params.delete("status");
    } else {
      params.set("status", status);
    }

    setSearchParams(params);
  };

  const openResolveModal = (ticket) => {
    setResolveTarget(ticket);
    setResolutionNote("");
  };

  const handleMarkInProgress = async (ticket) => {
    try {
      await updateSupportStatus({
        id: ticket._id,
        status: "in_progress",
      }).unwrap();

      refetchSupports();
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const getSupportActionItems = (record) => {
    const items = [
      {
        key: "view_notes",
        label: "View notes",
        icon: <FileTextOutlined />,
      },
    ];

    if (record.status === "in_progress") {
      items.push({
        key: "resolve",
        label: "Resolve",
        icon: <CheckCircleOutlined />,
      });
    } else if (record.status !== "resolved") {
      items.push({
        key: "in_progress",
        label: "In progress",
        icon: <ClockCircleOutlined />,
      });
    }

    return items;
  };

  const handleSupportActionClick = (record, actionKey, event) => {
    event?.domEvent?.stopPropagation?.();

    switch (actionKey) {
      case "view_notes":
        markEnquiriesAsSeen(userId, "support", [record._id]);
        setViewNotesTarget(record);
        break;
      case "in_progress":
        handleMarkInProgress(record);
        break;
      case "resolve":
        openResolveModal(record);
        break;
      default:
        break;
    }
  };

  const closeResolveModal = () => {
    setResolveTarget(null);
    setResolutionNote("");
  };

  const handleResolveSubmit = async () => {
    if (!resolveTarget) return;

    try {
      await updateSupportStatus({
        id: resolveTarget._id,
        status: "resolved",
        resolution_note: resolutionNote.trim(),
      }).unwrap();

      closeResolveModal();
      refetchSupports();

      notification.success({
        message: "Success",
        description: "A confirmation email will be sent to the user.",
      });
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const handleArchiveAction = async (id, type, isArchiving) => {
    try {
      if (type === "demo") {
        if (isArchiving) {
          await archiveDemo(id).unwrap();
        } else {
          await unarchiveDemo(id).unwrap();
        }
      } else if (type === "support") {
        if (isArchiving) {
          await archiveSupport(id).unwrap();
        } else {
          await unarchiveSupport(id).unwrap();
        }
      } else if (isArchiving) {
        await archiveContact(id).unwrap();
      } else {
        await unarchiveContact(id).unwrap();
      }
      notification.success({
        message: "Success",
        description: `Successfully ${isArchiving ? "archived" : "unarchived"} the ${type}`,
      });
    } catch (error) {
      notification.error({
        message: "Error",
        description: `Failed to ${isArchiving ? "archive" : "unarchive"} the ${type}`,
      });
    }
  };

  const handleDelete = async (id, type) => {
    try {
      if (type === "demo") {
        await deleteDemo(id).unwrap();
      } else if (type === "support") {
        await deleteSupport(id).unwrap();
      } else {
        await deleteContact(id).unwrap();
      }
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const getActionsMenuItems = (type) => {
    const items = [];

    if (type === "contact") {
      items.push({
        key: "view_message",
        label: "View message",
        icon: <FileTextOutlined />,
      });
    }

    items.push(
      {
        key: "archive",
        label: showArchived ? "Unarchive" : "Archive",
        icon: showArchived ? <ExportOutlined /> : <InboxOutlined />,
      },
      {
        key: "delete",
        label: "Delete",
        danger: true,
        icon: <DeleteOutlined />,
      },
    );

    return items;
  };

  const handleActionMenuClick = (record, actionKey, event, type) => {
    event?.domEvent?.stopPropagation?.();

    switch (actionKey) {
      case "view_message":
        markEnquiriesAsSeen(userId, "contacts", [record._id]);
        setViewMessageTarget(record);
        break;
      case "archive":
        handleArchiveAction(record._id, type, !showArchived);
        break;
      case "delete":
        Modal.confirm({
          title: `Delete ${type}`,
          content: `Are you sure you want to delete this ${type}?`,
          okText: "Yes",
          okType: "danger",
          cancelText: "No",
          onOk: () => handleDelete(record._id, type),
        });
        break;
      default:
        break;
    }
  };

  const renderActionsDropdown = (record, type) => (
    <div
      className="flex items-center justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      <Dropdown
        {...ACTIONS_DROPDOWN_PROPS}
        menu={{
          items: getActionsMenuItems(type),
          onClick: (event) =>
            handleActionMenuClick(record, event.key, event, type),
        }}
      >
        <button
          type="button"
          className="actions-dots-btn"
          onClick={(e) => e.stopPropagation()}
          aria-label={`Open actions for ${type}`}
        >
          <MoreOutlined />
        </button>
      </Dropdown>
    </div>
  );

  const getActionColumn = (type) => ({
    title: "",
    key: "actions",
    width: 80,
    align: "center",
    render: (_, record) => renderActionsDropdown(record, type),
  });

  const contactColumns = [
    {
      title: "Full Name",
      dataIndex: "full_name",
      key: "full_name",
      render: (text) => (
        <span className="font-medium text-gray-900">{text}</span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text) => <span className="text-gray-600">{text}</span>,
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      render: (text) => <span className="text-gray-900">{text}</span>,
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      render: (text) => (
        <span className="text-gray-600 block truncate max-w-[200px]">
          {text}
        </span>
      ),
    },
    {
      title: "Created",
      dataIndex: "date_created",
      key: "date_created",
      render: (date) => (
        <span className="text-gray-600">
          {date ? new Date(date).toLocaleDateString() : "N/A"}
        </span>
      ),
      sorter: (a, b) => new Date(a.date_created) - new Date(b.date_created),
    },
    {
      title: "Video",
      dataIndex: "video",
      key: "video",
      render: (url) =>
        url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            View Video
          </a>
        ) : (
          <span className="text-gray-400">No Video</span>
        ),
    },
    getActionColumn("contact"),
  ];

  const demoColumns = [
    {
      title: "Full Name",
      dataIndex: "full_name",
      key: "full_name",
      render: (text) => (
        <span className="font-medium text-gray-900">{text}</span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text) => <span className="text-gray-600">{text}</span>,
    },
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
      render: (text) => <span className="text-gray-900">{text}</span>,
    },
    {
      title: "Survey in Mind",
      dataIndex: "survey_in_mind",
      key: "survey_in_mind",
      render: (value) => (
        <span
          className={`inline-flex px-3 py-1 rounded-full text-xs capitalize ${
            value === "yes"
              ? "bg-green-100 text-green-700"
              : "bg-orange-100 text-orange-700"
          }`}
        >
          {value === "yes" ? "Yes" : "No"}
        </span>
      ),
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
      render: (date) => (
        <span className="text-gray-600">
          {date ? new Date(date).toLocaleDateString() : "N/A"}
        </span>
      ),
    },
    {
      title: "Created",
      dataIndex: "date_created",
      key: "date_created",
      render: (date) => (
        <span className="text-gray-600">
          {date ? new Date(date).toLocaleDateString() : "N/A"}
        </span>
      ),
      sorter: (a, b) => new Date(a.date_created) - new Date(b.date_created),
      defaultSortOrder: "descend",
    },
    getActionColumn("demo"),
  ];

  const supportColumns = [
    {
      title: "Full Name",
      dataIndex: "full_name",
      key: "full_name",
      render: (text) => (
        <span className="font-medium text-gray-900">{text}</span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text) => <span className="text-gray-600">{text}</span>,
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      render: (text) => (
        <span className="text-gray-900 block truncate max-w-[280px]">
          {text}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) =>
        status === "resolved" ? (
          <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-600 capitalize">
            Resolved
          </span>
        ) : (
          <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-yellow text-yellow-800 border border-yellow-500 capitalize">
            {status ? status.replace(/_/g, " ") : "Open"}
          </span>
        ),
    },
    {
      title: "Video",
      dataIndex: "video",
      key: "video",
      render: (url, record) => {
        const videoUrl = url || record.video_url;

        return videoUrl ? (
          <a
            href={videoUrl}
            target="_blank"
            rel="noreferrer"
            className="text-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            View Video
          </a>
        ) : (
          <span className="text-gray-400">No Video</span>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => (
        <div
          className="flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown
            {...ACTIONS_DROPDOWN_PROPS}
            menu={{
              items: getSupportActionItems(record),
              onClick: (event) =>
                handleSupportActionClick(record, event.key, event),
            }}
          >
            <button
              type="button"
              className="actions-dots-btn"
              onClick={(e) => e.stopPropagation()}
              aria-label="Open actions for support ticket"
            >
              <MoreOutlined />
            </button>
          </Dropdown>
        </div>
      ),
    },
  ];

  const filteredSupportList = useMemo(() => {
    if (supportStatusFilter === "all") {
      return supportList;
    }

    return supportList.filter(
      (ticket) => normalizeSupportStatus(ticket.status) === supportStatusFilter,
    );
  }, [supportList, supportStatusFilter]);

  const isContactsTab = activeTab === "contacts";
  const isSupportTab = activeTab === "support";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center rounded-2xl bg-secondary p-1 w-fit">
          <button
            type="button"
            onClick={() => setEnquiriesTab("contacts")}
            className={`px-4 py-2 rounded-xl text-sm font-medium border-0 cursor-pointer ${
              isContactsTab
                ? "bg-white text-black shadow-sm"
                : "bg-transparent text-gray-600"
            }`}
          >
            {renderTabBadge("Contact Us Enquiries", unseenCounts.contacts)}
          </button>
          <button
            type="button"
            onClick={() => setEnquiriesTab("demos")}
            className={`px-4 py-2 rounded-xl text-sm font-medium border-0 cursor-pointer ${
              activeTab === "demos"
                ? "bg-white text-black shadow-sm"
                : "bg-transparent text-gray-600"
            }`}
          >
            {renderTabBadge("Demo Requests", unseenCounts.demos)}
          </button>
          <button
            type="button"
            onClick={() => setEnquiriesTab("support")}
            className={`px-4 py-2 rounded-xl text-sm font-medium border-0 cursor-pointer ${
              isSupportTab
                ? "bg-white text-black shadow-sm"
                : "bg-transparent text-gray-600"
            }`}
          >
            {renderTabBadge("Support", unseenCounts.support)}
          </button>
        </div>

        {isSupportTab ? (
          <div className="flex bg-secondary rounded-xl p-1.5">
            {SUPPORT_STATUS_FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => setSupportStatus(filter.value)}
                className={`px-4 py-2 rounded-xl text-sm font-medium border-0 cursor-pointer ${
                  supportStatusFilter === filter.value
                    ? "bg-primary text-white"
                    : "bg-transparent text-gray-600"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        ) : (
          <div className="flex bg-secondary rounded-xl p-1.5">
            <button
              type="button"
              onClick={() => setShowArchived(false)}
              className={`px-4 py-2 rounded-xl text-sm font-medium border-0 cursor-pointer ${
                !showArchived
                  ? "bg-primary text-white"
                  : "bg-transparent text-gray-600"
              }`}
            >
              Active
            </button>
            <button
              type="button"
              onClick={() => setShowArchived(true)}
              className={`px-4 py-2 rounded-xl text-sm font-medium border-0 cursor-pointer ${
                showArchived
                  ? "bg-primary text-white"
                  : "bg-transparent text-gray-600"
              }`}
            >
              Archived
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl overflow-hidden">
        {isContactsTab && (
          <Table
            columns={contactColumns}
            dataSource={contactList}
            loading={contactsLoading}
            rowKey="_id"
            pagination={buildConditionalPagination(contactList)}
            className="forms-table"
            rowClassName="hover:bg-gray-50"
            scroll={{ x: "100%" }}
          />
        )}
        {isSupportTab && (
          <Table
            columns={supportColumns}
            dataSource={filteredSupportList}
            loading={supportsLoading}
            rowKey="_id"
            pagination={buildConditionalPagination(filteredSupportList)}
            className="forms-table"
            rowClassName="hover:bg-gray-50"
            scroll={{ x: "100%" }}
          />
        )}
        {!isContactsTab && !isSupportTab && (
          <Table
            columns={demoColumns}
            dataSource={demoList}
            loading={demosLoading}
            rowKey="_id"
            pagination={buildConditionalPagination(demoList)}
            className="forms-table"
            rowClassName="hover:bg-gray-50"
            scroll={{ x: "100%" }}
          />
        )}
      </div>

      <Modal
        open={!!resolveTarget}
        title="Resolution "
        okText="Resolve"
        onOk={handleResolveSubmit}
        onCancel={closeResolveModal}
        okButtonProps={{
          disabled: !resolutionNote.trim(),
          size: "small",
          className: "!h-9 !px-5 !text-sm",
        }}
        cancelButtonProps={{ size: "small", className: "!h-9 !px-5 !text-sm" }}
        destroyOnClose
      >
        <p className="text-gray-600 mb-3">
          Add a resolution note for{" "}
          <span className="font-medium text-gray-900">
            {resolveTarget?.full_name || "this ticket"}
          </span>{" "}
          describing how the issue was resolved.
        </p>
        <Input.TextArea
          value={resolutionNote}
          onChange={(e) => setResolutionNote(e.target.value)}
          rows={5}
          placeholder="Enter the resolution note..."
        />
      </Modal>

      <Modal
        open={!!viewNotesTarget}
        title="Resolution note"
        footer={null}
        onCancel={() => setViewNotesTarget(null)}
        destroyOnClose
        styles={{ body: { maxHeight: "60vh", overflowY: "auto" } }}
      >
        {viewNotesTarget?.resolution_note ? (
          <p className="whitespace-pre-wrap text-gray-700">
            {viewNotesTarget.resolution_note}
          </p>
        ) : (
          <p className="text-gray-400">
            No resolution note has been added yet.
          </p>
        )}
      </Modal>

      <Modal
        open={!!viewMessageTarget}
        title="Message"
        footer={null}
        onCancel={() => setViewMessageTarget(null)}
        destroyOnClose
        styles={{ body: { maxHeight: "60vh", overflowY: "auto" } }}
      >
        {viewMessageTarget?.message ? (
          <p className="whitespace-pre-wrap text-gray-700">
            {viewMessageTarget.message}
          </p>
        ) : (
          <p className="text-gray-400">No message was provided.</p>
        )}
      </Modal>
    </div>
  );
};

export default Enquiries;
